import type {
  EventLevelCompoundMarketTemplateId,
  EventLevelMarketTemplateId,
  MarketLevelCompoundMarketTemplateId,
  MarketLevelMarketTemplateId
} from '@ps/isp-web-services-types';
import React from 'react';
import { useSelector } from 'react-redux';

import { useEventMarketTemplatesWidget } from '@swf/sports/models/market-templates/eventBased';
import type {
  CompoundMarketTemplateDisplayConfig,
  CompoundMarketTemplateProps,
  EventLevelCompoundMarketTemplateProps,
  EventLevelTemplateId,
  MarketDisplayConfig,
  MarketLevelCompoundMarketTemplateProps,
  MarketLevelTemplateId,
  MarketTemplateElementType,
  MarketTemplateProps,
  SingleMarketTemplateDisplayConfig,
  SingleMarketTemplateProps
} from '@swf/sports/types/templates';
import { getValidMarketTemplateTypes } from '@swf/sports/utils/getValidMarketTemplateTypes';
import { isCompoundMarketTemplateProps } from '@swf/sports/utils/isCompoundMarketTemplateProps';

import AntePostList from './templates/AntePostList/AntePostList';
import CorrectScore from './templates/CorrectScore/CorrectScore';
import EventOutright from './templates/EventOutright/EventOutright';
import EventRow from './templates/EventRow/EventRow';
import EventTable from './templates/EventTable/EventTable';
import GameLines from './templates/GameLines/GameLines';
import Grid from './templates/Grid/Grid';
import Link from './templates/Link/Link';
import List from './templates/List/List';
import NamedList from './templates/NamedList/NamedList';
import OddsBoost from './templates/OddsBoost/OddsBoost';
import RaceCard from './templates/RaceCard/RaceCard';
import Row from './templates/Row/Row';
import Table from './templates/Table/Table';
import TimedList from './templates/TimedList/TimedList';

const SINGLE_MARKET_TEMPLATE_MAP: Record<
  MarketLevelMarketTemplateId | EventLevelMarketTemplateId,
  MarketTemplateElementType<SingleMarketTemplateProps>
> = {
  GRID: Grid,
  LIST: List,
  ODDS_BOOST: OddsBoost,
  ROW: Row,
  ANTE_POST_LIST: AntePostList,
  TIMED_LIST: TimedList,
  NAMED_LIST: NamedList,
  RACE_CARD: RaceCard,
  EVENT_ROW: EventRow,
  EVENT_OUTRIGHT: EventOutright,
  LINK: Link,
  CORRECT_SCORE: CorrectScore
};

const COMPOUND_MARKET_TEMPLATE_MAP: Record<
  MarketLevelCompoundMarketTemplateId,
  MarketTemplateElementType<MarketLevelCompoundMarketTemplateProps>
> &
  Record<
    EventLevelCompoundMarketTemplateId,
    MarketTemplateElementType<EventLevelCompoundMarketTemplateProps>
  > = {
  TABLE: Table,
  EVENT_TABLE: EventTable,
  GAME_LINES: GameLines
};

export const MarketTemplate: React.FC<
  MarketTemplateProps & {
    templateId: MarketLevelTemplateId | EventLevelTemplateId;
    templateDisplayConfig?: Partial<MarketDisplayConfig>;
  }
> = ({ templateId, templateDisplayConfig = {}, ...restProps }) => {
  const { widgetSelect } = useEventMarketTemplatesWidget();
  const marketTemplateData = useSelector(widgetSelect.marketTemplateData);
  const validMarketTemplateTypes = getValidMarketTemplateTypes(marketTemplateData);

  if (isCompoundMarketTemplateProps(restProps)) {
    const Component: MarketTemplateElementType<CompoundMarketTemplateProps> =
      COMPOUND_MARKET_TEMPLATE_MAP[
        templateId as MarketLevelCompoundMarketTemplateId | EventLevelCompoundMarketTemplateId
      ] as MarketTemplateElementType<CompoundMarketTemplateProps>;

    if (validMarketTemplateTypes.includes(Component.marketTemplateType)) {
      return (
        <Component
          {...restProps}
          displayConfig={
            templateDisplayConfig[
              templateId as Extract<
                MarketLevelCompoundMarketTemplateId | EventLevelCompoundMarketTemplateId,
                keyof MarketDisplayConfig
              >
            ] as CompoundMarketTemplateDisplayConfig
          }
        />
      );
    }
  } else if ('marketId' in restProps) {
    const Component: MarketTemplateElementType<SingleMarketTemplateProps> =
      SINGLE_MARKET_TEMPLATE_MAP[
        templateId as MarketLevelMarketTemplateId | EventLevelMarketTemplateId
      ];

    if (validMarketTemplateTypes.includes(Component.marketTemplateType)) {
      return (
        <Component
          {...restProps}
          displayConfig={
            templateDisplayConfig[
              templateId as Extract<
                MarketLevelMarketTemplateId | EventLevelMarketTemplateId,
                keyof MarketDisplayConfig
              >
            ] as SingleMarketTemplateDisplayConfig
          }
        />
      );
    }
  }

  return null;
};
